/**
 * PURPOSE: Pestaña "Compañías y deportes" del panel Master.
 * RESPONSIBILITIES: Elegir una compañía administrable (compañía efectiva por pestaña vía
 *   ?idcompany=, FTR-0012) y administrar sus deportes habilitados. Solo se ofrecen las compañías
 *   que el actor puede administrar; las escrituras viajan con X-Hayllis-Company.
 * COLLABORATORS: src/hooks/useEffectiveCompany.js, adminCompanyService.js, CompanySportsManager.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/AdminCompanySportsTab.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { getApiErrorMessage, listCompanySports } from "../../services/adminCompanyService.js";
import { useEffectiveCompany } from "../../hooks/useEffectiveCompany.js";
import CompanySportsManager from "./CompanySportsManager.jsx";

const STATUS_MESSAGES = {
  forbidden: "Tu usuario no puede administrar compañías.",
  error: "No se pudieron cargar las compañías que administras.",
  empty: "No tienes compañías activas disponibles para administrar.",
  invalid: "La compañía indicada no está disponible para tu usuario.",
};

const AdminCompanySportsTab = () => {
  const { status, effectiveCompanyId, company, companies, setCompany } = useEffectiveCompany();
  const [sports, setSports] = useState([]);
  const [loadingSports, setLoadingSports] = useState(false);

  const loadSports = useCallback(async () => {
    if (!effectiveCompanyId) {
      setSports([]);
      return;
    }

    setLoadingSports(true);
    try {
      setSports(await listCompanySports(effectiveCompanyId));
    } catch (error) {
      console.error("Error al cargar deportes de la compañía", error);
      setSports([]);
      toast.error(
        getApiErrorMessage(error, "No se pudieron cargar los deportes de la compañía.")
      );
    } finally {
      setLoadingSports(false);
    }
  }, [effectiveCompanyId]);

  useEffect(() => {
    loadSports();
  }, [loadSports]);

  const loadingCompanies = status === "loading" || status === "redirect";

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">
            Compañías y deportes
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Selecciona una compañía para administrar los deportes que tiene habilitados.
          </p>
        </div>

        {STATUS_MESSAGES[status] && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {STATUS_MESSAGES[status]}
          </div>
        )}

        <label className="block max-w-2xl text-sm font-medium text-slate-700 dark:text-slate-200">
          Compañía
          <select
            value={effectiveCompanyId ? String(effectiveCompanyId) : ""}
            onChange={(event) => setCompany(event.target.value)}
            disabled={loadingCompanies || companies.length === 0}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100"
          >
            <option value="">
              {loadingCompanies ? "Cargando compañías..." : "Seleccione una compañía"}
            </option>
            {companies.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
                {item.abbreviation ? ` (${item.abbreviation})` : ""}
              </option>
            ))}
          </select>
        </label>

        {company && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700">
              ID #{company.id}
            </span>
            <span>{company.name}</span>
            <span>·</span>
            <span>{sports.length} deporte{sports.length === 1 ? "" : "s"}</span>
          </div>
        )}
      </section>

      {status !== "ready" || !effectiveCompanyId ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-500">
          Selecciona una compañía para continuar.
        </div>
      ) : loadingSports ? (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500">
          Cargando deportes de la compañía...
        </div>
      ) : (
        <CompanySportsManager
          key={effectiveCompanyId}
          idcompany={effectiveCompanyId}
          sports={sports}
          onChanged={loadSports}
        />
      )}
    </div>
  );
};

export default AdminCompanySportsTab;
